import { object } from '@art-suite/art-core-ts';
import {
  ApiAllOrgSettings,
  ApiOrgSettings,
  getAllOrgSettingsQueryKey,
  getOrgSettingQueryKey,
  getOrgSettingsQueryKey,
  getTaskModifierDisplayNames,
  KaiProApiClient,
  TaskModifierDisplayNames,
} from '@kaiizen/kai-pro-api';
import { TypedCacheEntry, useCachedQuery, useQueryCacheMutation } from '@kaiizen/react-persistent-cache';
import { useMemo } from 'react';
import { useKaiProApiClient } from '../useKaiProApiClient';

const getOrgSettingsMutators = (kaiProApiClient: KaiProApiClient) => ({
  update: useQueryCacheMutation(
    async (settingsKey: string, updates: Record<string, any>) => kaiProApiClient.updateOrgSettingsByKey(settingsKey, updates),
    {
      onSuccess: async (orgSetting, queryCache) => {
        queryCache.cache.set(getOrgSettingQueryKey(orgSetting.id), orgSetting);
        const existing = await queryCache.cache.getEntry(getOrgSettingsQueryKey(kaiProApiClient.token)) as TypedCacheEntry<ApiOrgSettings[]> | undefined;
        if (existing) {
          queryCache.cache.set(getOrgSettingsQueryKey(kaiProApiClient.token), [
            orgSetting,
            ...existing.value.filter(setting => setting.id !== orgSetting.id),
          ]);
        }
      },
    },
  ).mutate,
});

const getAllOrgSettingsMutators = (kaiProApiClient: KaiProApiClient) => ({
  update: useQueryCacheMutation(
    async (updates: ApiAllOrgSettings) => kaiProApiClient.updateAllOrgSettings(updates),
    {
      onSuccess: async (orgSettings, queryCache) => {
        queryCache.cache.set(getAllOrgSettingsQueryKey(kaiProApiClient.token), orgSettings);
      },
    },
  ).mutate,
});

export const useOrgSettings = () => {
  const kaiProApiClient = useKaiProApiClient();
  const orgSettingsQueryKey = getOrgSettingsQueryKey(kaiProApiClient.token);
  const allOrgSettingsQueryKey = getAllOrgSettingsQueryKey(kaiProApiClient.token);

  const query = useCachedQuery(
    orgSettingsQueryKey,
    async (key, queryCache) => {
      const orgSettings = await kaiProApiClient.getOrgSettings();
      orgSettings.forEach(orgSetting => {
        queryCache.cache.set(getOrgSettingQueryKey(orgSetting.id), orgSetting);
      });
      return orgSettings;
    },
    { enabled: !!kaiProApiClient.token },
  );

  const orgSettings = query.data;
  const orgSettingsByKey = useMemo(
    () => orgSettings ? object(orgSettings, { withKey: ({ key }) => key }) : undefined,
    [orgSettings],
  );

  return {
    ...query,
    orgSettings,
    orgSettingsByKey,
    orgSettingsMutators: getOrgSettingsMutators(kaiProApiClient),
  };
};

export const useAllOrgSettings = () => {
  const kaiProApiClient = useKaiProApiClient();
  const allOrgSettingsQueryKey = getAllOrgSettingsQueryKey(kaiProApiClient.token);

  const query = useCachedQuery(
    allOrgSettingsQueryKey,
    async () => {
      const orgSettings = await kaiProApiClient.getAllOrgSettings();
      return orgSettings;
    },
    { enabled: !!kaiProApiClient.token },
  );

  return {
    ...query,
    allOrgSettings: query.data as ApiAllOrgSettings | undefined,
    allOrgSettingsMutators: getAllOrgSettingsMutators(kaiProApiClient),
  };
};

/** The org's labels for its deferred and tenant-responsibility modifiers, so reports say what the app says. */
export const useTaskModifierDisplayNames = (): TaskModifierDisplayNames => {
  const { allOrgSettings } = useAllOrgSettings();
  const modifiers = allOrgSettings?.taskSettings?.modifiers;

  return useMemo(() => getTaskModifierDisplayNames(modifiers), [modifiers]);
};
